/* import __COLOCATED_TEMPLATE__ from './shopify-install-messenger-modal.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type { TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type RouterService from '@ember/routing/router-service';
import type NotificationsService from 'embercom/services/notifications-service';
import { get } from 'embercom/lib/ajax';
import { captureException } from '@sentry/browser';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';

interface Args {
  onModalClose: () => void;
}

interface Signature {
  Args: Args;
  Element: never;
}

interface IntercomEventService {
  trackAnalyticsEvent(event: Record<string, unknown>): void;
}

type VerifyInstallResponse = {
  anonymous_request_at: string | null;
  identified_request_at: string | null;
};

const WISTIA_ID = '883eqxmxjo';

export default class ShopifyInstallMessengerModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intercomEventService: IntercomEventService;
  @service declare router: RouterService;
  @service declare realTimeEventService: {
    on: Function;
    off: Function;
    subscribeTopics: Function;
    unsubscribeTopics: Function;
  };

  @tracked checkedAtLeastOnce = false;
  @tracked isMessengerInstalled = false;

  stepAssetUrl = assetUrl(
    '/assets/images/onboarding/getting-started/shopify_onboarding_install_messenger.png',
  );
  videoUrl = `https://fast.wistia.net/embed/iframe/${WISTIA_ID}?videoFoam=true&autoPlay=true`;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.addNexusEventListeners();
  }

  willDestroy() {
    super.willDestroy();
    this.removeNexusEventListeners();
  }

  addNexusEventListeners() {
    this.realTimeEventService.subscribeTopics(['mark-step-as-completed']);
    this.realTimeEventService.on('MarkStepAsCompleted', this, 'handleStepCompleted');
  }

  removeNexusEventListeners() {
    this.realTimeEventService.unsubscribeTopics(['mark-step-as-completed']);
    this.realTimeEventService.off('MarkStepAsCompleted', this, 'handleStepCompleted');
  }

  handleStepCompleted(event: { step_identifier: string }) {
    if (event.step_identifier === 'shopify_onboarding_install_messenger') {
      this.args.onModalClose();
    }
  }

  get isChecking() {
    return taskFor(this.checkInstallationTask).isRunning;
  }

  get showSuccessBanner() {
    return !this.isChecking && this.isMessengerInstalled && this.checkedAtLeastOnce;
  }

  get showFailureBanner() {
    return !this.isChecking && !this.isMessengerInstalled && this.checkedAtLeastOnce;
  }

  @action
  openAdvancedSettings() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'advanced_settings',
      place: 'shopify-onboarding-modal',
      section: 'getting-started',
      platform: 'web',
    });
    this.args.onModalClose();
    this.router.transitionTo('apps.app.settings.channels.messenger.install');
  }

  @action
  checkInstallation() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'check_messenger_installation',
      place: 'shopify-onboarding-modal',
      section: 'getting-started',
      platform: 'web',
    });
    return taskFor(this.checkInstallationTask).perform();
  }

  @task({ restartable: true })
  *checkInstallationTask(): TaskGenerator<void> {
    try {
      let response: VerifyInstallResponse = yield get(
        `/ember/messenger_install_verification/verify_any_install?app_id=${this.appService.app.id}`,
      );

      this.isMessengerInstalled = !!(
        response.anonymous_request_at || response.identified_request_at
      );
      this.checkedAtLeastOnce = true;
    } catch (error: unknown) {
      captureException(error, {
        tags: {
          responsibleTeam: 'team-self-serve',
        },
      });
      this.notificationsService.notifyError(
        this.intl.t(
          'apps.app.settings.installation.installation.new.installation-status.manual-check-error-message',
        ),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Onboarding::GettingStarted::ShopifyInstallMessengerModal': typeof ShopifyInstallMessengerModal;
  }
}
